import { useQuery } from '@tanstack/react-query';
import { directoryApi, type ProjectDirectory } from '@/lib/apiClient';

/** A person on the job's Contacts tab, as a picker offers them (#598 step 2). */
export interface JobContactOption {
  contactId: number;
  name: string;
  email: string | null;
  companyName: string | null;
  /** Their role(s) on this job, for telling two people apart: "Architect", "Owner's Rep". */
  roles: string;
}

/**
 * The job's outside contacts, one per person (someone on the job in two roles
 * is offered once), ordered as the Contacts tab groups them.
 */
export function jobContactOptions(directory: ProjectDirectory | undefined): JobContactOption[] {
  if (!directory) return [];
  const label = (key: string) => directory.roles.find((r) => r.key === key)?.label ?? key;
  const byPerson = new Map<number, JobContactOption>();
  for (const c of directory.contacts) {
    const had = byPerson.get(c.person.id);
    if (had) {
      had.roles = `${had.roles}, ${label(c.role)}`;
      continue;
    }
    byPerson.set(c.person.id, {
      contactId: c.person.id,
      name: c.person.name,
      email: c.person.email,
      companyName: c.company?.name ?? null,
      roles: label(c.role),
    });
  }
  return [...byPerson.values()];
}

/**
 * The job's contacts, read through the same query the Contacts tab uses, so
 * they're shared and an add or edit there shows here without a second fetch.
 */
export function useJobContacts(projectId: number, enabled = true) {
  const q = useQuery({
    queryKey: ['project-contacts', projectId],
    queryFn: () => directoryApi.forProject(projectId),
    enabled,
  });
  return { options: jobContactOptions(q.data), isLoading: q.isLoading, isError: q.isError, isSuccess: q.isSuccess };
}

/** How a contact reads in a picker: "Ana Architect · Bluegrass Design". */
export const contactLine = (c: { name: string | null; companyName: string | null }) =>
  [c.name ?? '—', c.companyName].filter(Boolean).join(' · ');
